import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  ImputableProjectForSelectFragment,
  ImputableProjectSelectorDocument,
  ImputableProjectSelectorQuery,
} from 'generated/resources/graphql'

import { EntitySelector } from 'modules/core/components/EntitySelector'
import i18n from 'modules/core/lib/i18n'

import { SubprojectSelector } from 'modules/project_management/components/selectors/SubprojectSelector'

import {
  ImputableProjectSelectorMultipleProps,
  ImputableProjectSelectorSingleWithSubprojectsProps,
  ImputableProjectSelectorSingleWithoutSubprojectsProps,
} from './types'

const ImputableProjectItem = ({ project }: { project: ImputableProjectForSelectFragment }) => {
  return (
    <Box gap="s2">
      <Box flexDirection="row" alignItems="center" gap="s8">
        <Text.Normal weight="medium" color="grey1000">
          {project.name}
        </Text.Normal>
      </Box>
      {project.code && (
        <Box flexDirection="row">
          <Text.Normal size="200">{project.code}</Text.Normal>
        </Box>
      )}
    </Box>
  )
}

export const ImputableProjectSelector: React.FC<
  | ImputableProjectSelectorSingleWithSubprojectsProps
  | ImputableProjectSelectorSingleWithoutSubprojectsProps
  | ImputableProjectSelectorMultipleProps
> = (props) => {
  const { value, multiple, forEmployeeId, assigned = true, onlyActive = true } = props

  const variables = {
    employeeIds: forEmployeeId ? [forEmployeeId] : [],
    assigned,
    onlyActive,
  }

  const extractConnection = (data: ImputableProjectSelectorQuery) => {
    return data.projectManagement.imputableProjectsConnection
  }

  const renderEntity = (entity: ImputableProjectForSelectFragment) => {
    return <ImputableProjectItem project={entity} />
  }

  // We need Typescript to evaluate this variable and to be sure that will not change, so later on,
  // we can use it to check if we should render the SubprojectSelector component and safely access other props
  const withSubprojects =
    !multiple && props.subprojectValue !== undefined && !!props.onChangeSubproject

  const commonProps = {
    document: ImputableProjectSelectorDocument,
    extractConnection,
    label: i18n.t('project_management.modals.add_project.select_project'),
    renderEntity,
    variables,
  }

  if (multiple) {
    const selectorProps = { ...commonProps, ...props }

    return (
      <EntitySelector.Multiple
        {...selectorProps}
        renderValue={(imputableProjects) => (
          <Text.Normal ellipsis>
            {imputableProjects.length > 0
              ? imputableProjects.map((imputableProject) => imputableProject.name).join(', ')
              : i18n.t('common.select_multiple')}
          </Text.Normal>
        )}
      />
    )
  }

  const selectorProps = { ...commonProps, ...props }

  return (
    <Box gap="s24">
      <EntitySelector
        {...selectorProps}
        renderValue={(entity) =>
          entity !== null ? (
            <Text.Normal>{entity.name}</Text.Normal>
          ) : (
            <Text.Normal>{i18n.t('common.select')}</Text.Normal>
          )
        }
      />
      {withSubprojects && value && (value?.subprojectsConnection.totalCount || 0 > 0) && (
        <SubprojectSelector
          imputableProjectId={value.id}
          value={props.subprojectValue}
          onChange={props.onChangeSubproject}
          disabled={props.disabled}
        />
      )}
    </Box>
  )
}
